import { consumeAppliedPageKeys } from './applied-page-keys'
// Native replication owns card rows, demand, receipts and cursor.
import { safe } from '@w/utils'
import { createReplicationErrorReporter, isReplicationOffline, isReplicationOutcome, isWorkspaceAccessRejection } from './workspace-replication-errors'
import type { TursoWorkerOwnership, TursoWorkerDatabaseDriver } from '@w/db-turso-browser-adapter/worker'
import type { Tel } from '@w/tel'
import { ReplicationInvariantError } from '@w/db-replication'
import { ReplicaTransportError, readReplicaState } from '@w/db-turso-browser-adapter/worker'
import type { WorkspaceDb } from './workspace-tables'
import { completeLearnerBootstrap } from './learner-actions'
import type { TransportStatusDriver } from '../router/replica-transport-status'

export type NativeWorkspaceSummary = { cursor: bigint }
/** A pulled page failed to apply: the engine applies nothing further (pushes continue) until a new app version or a re-download. */
export type SyncHalt = { error: string }

/** The binding exposes the persisted apply failure until reset. */
export async function readSyncHalt(driver: Pick<TursoWorkerDatabaseDriver, 'subscribeReplica'>): Promise<SyncHalt | null> {
  const state = await readReplicaState(driver)
  return state.syncError ? { error: state.syncError } : null
}
export async function openWorkspaceReplication(options: {
  tel: Tel; db: WorkspaceDb; userId: string; spaceId: string
  exchange?: (signal?: AbortSignal, tel?: Tel) => Promise<bigint>
  ownership?: TursoWorkerOwnership; start?: boolean; onOwner?: () => void
  onError?: (error: unknown) => void; onApplied?: () => void; onPending?: (pending: boolean) => void
  beforeExchange?: () => Promise<void>; afterExchange?: () => Promise<void>
  /** Called after every exchange with the engine's persisted pull-apply failure, or null. */
  onHalt?: (halt: SyncHalt | null) => void
}) {
  const driver = safe(options.db.driver, 'Workspace driver required') as TursoWorkerDatabaseDriver & TransportStatusDriver
  if (!driver.subscribeReplica || !driver.exchange) throw new Error('Native replication driver required')
  
  await workspaceBootstrapReadiness(options.db, options.userId)
  const nativeExchange = driver.exchange.bind(driver)
  const exchange = options.exchange ?? ((signal?: AbortSignal, tel = options.tel) => nativeExchange({ tel, signal }))
  const lifetime = new AbortController()
  const demands = new Set<Promise<NativeWorkspaceSummary>>()
  const reporter = createReplicationErrorReporter(options.tel, options.spaceId)
  let captureValidated = false
  async function requireCapture() {
    const [capture] = await driver.query<{ mode: string }>('PRAGMA capture_data_changes_conn', undefined, { tel: options.tel })
    if (capture?.mode !== 'full') throw new ReplicationInvariantError('INV: replication requires capture full')
    captureValidated = true
  }
  /** While the engine is halted every round ends in "sync halted"; the "Sync stopped" alert is the one signal, so those rounds report nothing. */
  let halted = false
  const attempts = new Set<(error: unknown) => void>()
  /** Idle notifications whose applied pages are still being consumed; onApplied has not run yet. */
  const applying = new Set<Promise<void>>()
  function failed(error: unknown, diagnosticOwned = false) {
    if (lifetime.signal.aborted) return
    const stops = isWorkspaceAccessRejection(error) || error instanceof ReplicaTransportError && (error.code === 'generation_reset' || error.code === 'eligibility_expired')
    if (halted && !stops) return
    reporter.failure(error, diagnosticOwned)
    options.onError?.(error)
    if (stops) { stopReason = { error }; stop() }
  }
  /** The answer that stopped the handle; a sync aborted by that stop settles with it, not the handle's own abort. */
  let stopReason: { error: unknown } | undefined
  const releaseNative = await driver.subscribeReplica(state => {
    if (lifetime.signal.aborted) return
    options.onPending?.(state.status === 'exchanging')
    if (state.status === 'error') void (async () => {
      halted = await readSyncHalt(driver).then(halt => halt !== null, (readError: unknown) => { failed(readError, true); return false })
      failed(state.error, true)
    })()
    else if (state.status === 'idle' && state.cursor !== null) {
      const task = options.tel.span({ name: 'replica.consume-applied-pages', input: null }, async () => {
        const applied = await consumeAppliedPageKeys(driver)
        reporter.success(); if (applied) options.onApplied?.()
      }).catch((error: unknown) => { failed(error, true) })
      applying.add(task)
      void task.then(() => applying.delete(task))
    }
  })
  const releaseTransport = driver.subscribeTransport?.(error => {
    if (error === null) return
    // An in-flight explicit sync settles with this answer before a 410 stop aborts the handle;
    // the caller then runs the reset (replication-semantics.md:132 I9) instead of seeing "handle closed".
    for (const reject of attempts) reject(error)
    failed(error, true)
  })
  const releaseOwnership = options.ownership?.subscribe(owned => {
    if (owned && !lifetime.signal.aborted) options.onOwner?.()
  })
  function sync(): Promise<NativeWorkspaceSummary | null> {
    if (lifetime.signal.aborted) return Promise.resolve(null)
    const demand = options.tel.span({ name: 'replica.request', input: null }, async tel => {
      try {
      await requireCapture()
      lifetime.signal.throwIfAborted()
      await options.beforeExchange?.()
      // Source: libs/platform/infra/db-replication/docs/replication-semantics.md:435
      // A failed halt read is an error, never "not halted".
      const reportHalt = async () => { const halt = await readSyncHalt(driver); halted = halt !== null; options.onHalt?.(halt) }
      const attempt = Promise.withResolvers<never>()
      attempts.add(attempt.reject)
      const cursor = await Promise.race([exchange(lifetime.signal, tel), attempt.promise]).finally(() => { attempts.delete(attempt.reject) }).then(async value => { await reportHalt(); return value }, async (exchangeError: unknown) => {
        // The exchange's answer stays the sync's error, so the caller classifies it (a 410 runs the I9 reset);
        // a failed halt read is reported on its own, as the error-state listener above does.
        await reportHalt().catch((readError: unknown) => { failed(readError, true) })
        throw exchangeError
      }).catch(async (exchangeError: unknown) => {
        // A refusal receipt can commit before the subsequent pull fails.
        await options.afterExchange?.().catch((recoveryError: unknown) => {
          throw new SuppressedError(recoveryError, exchangeError, 'Failed to preserve exchange recovery')
        })
        throw exchangeError
      })
      const applied = await consumeAppliedPageKeys(driver)
      if (applied) options.onApplied?.()
      await options.afterExchange?.()

      lifetime.signal.throwIfAborted()
      return { cursor }
      } catch (error) {
        if (!lifetime.signal.aborted || !(error instanceof Error && error.name === 'AbortError')) throw error
        // A 410/403 that stopped the handle before this sync registered its attempt (requireCapture/beforeExchange):
        // the caller needs that answer to reset (replication-semantics.md:132 I9), not the abort it caused.
        if (stopReason) throw stopReason.error
        tel.currentSpan.update({ attributes: { 'replica.request.outcome': 'cancelled' } })
        return { cancelled: error }
      }
    }).then(result => {
      if (result.cancelled !== undefined) throw result.cancelled
      return { cursor: result.cursor }
    }).catch((error: unknown) => { failed(error, true); throw error })
    demands.add(demand)
    return demand.finally(() => { demands.delete(demand) })
  }
  function stop() {
    if (lifetime.signal.aborted) return
    lifetime.abort(new DOMException('Workspace replication handle closed', 'AbortError'))
    releaseNative(); releaseTransport?.(); releaseOwnership?.()
  }
  const replication = { sync, stop,
    /** Resolves once every idle round seen so far has consumed its applied pages and called onApplied. */
    async applied() { while (applying.size > 0) await Promise.all(applying) },
    bootstrap: () => options.tel.span({ name: 'replica.bootstrap', input: null }, async () => {
      lifetime.signal.throwIfAborted()
      // Startup must validate capture even when the opener already exchanged.
      // Every later sync validates again; its following bootstrap reuses that check.
      if (!captureValidated) await requireCapture()
      return workspaceBootstrapReadiness(options.db, options.userId)
    }).catch((error: unknown) => { failed(error, true); throw error }), async close() { stop(); await Promise.allSettled(demands) } }

  if (options.start !== false) {
    try { await sync() }
    catch (error) {
      if (error instanceof ReplicationInvariantError) { await replication.close(); throw error }
      if (!isReplicationOffline(error) && !isReplicationOutcome(error)) failed(error, true)
    }
  }
  return replication
}

/** Set session before exchange; replay derives cards in the page transaction. */
export async function workspaceBootstrapReadiness(db: WorkspaceDb, userId: string) {
  await completeLearnerBootstrap(db, userId)
  return { ready: true, label: null }
}
